import React, { useEffect, useRef, useState } from 'react';

const MILESTONES = [
  {
    step: '01',
    title: 'A Vision Born',
    text:
      'Founded to revolutionize real estate in India, 360Coordinates emerged as a trusted platform dedicated to simplifying property transactions for buyers, sellers, and renters.',
  },
  {
    step: '02',
    title: 'Trust & Transparency',
    text:
      'We committed to verified listings, transparent pricing, and expert support — connecting property seekers with dream homes and commercial spaces across India.',
  },
  {
    step: '03',
    title: 'Leading the Way',
    text:
      'Today, 360Coordinates stands among India\'s leading real estate platforms — innovating continuously to serve the evolving needs of the Indian property market.',
  },
];

const HIGHLIGHTS = ['Verified Listings', 'Pan-India Reach', 'Expert Support'];

const STORY_IMAGES = [
  {
    src: `${process.env.PUBLIC_URL}/story/r3-discover-homes.png`,
    alt: 'Searching and discovering homes on a property map',
    tag: 'Discover Homes',
    caption: 'Browse verified properties across India',
  },
  {
    src: `${process.env.PUBLIC_URL}/story/r2-our-story.png`,
    alt: 'Building the future of real estate across India',
    tag: 'Our Story',
    caption: 'Building trust in every transaction',
  },
  {
    src: `${process.env.PUBLIC_URL}/story/r4-happy-customer.png`,
    alt: 'Happy couple finding their dream home with a real estate agent',
    tag: 'Happy Customers',
    caption: 'Helping families find their dream homes',
  },
  {
    src: `${process.env.PUBLIC_URL}/story/r1-agent-builder.jpg`,
    alt: 'Real estate agents and builders on the platform',
    tag: 'Agents & Builders',
    caption: 'Empowering professionals nationwide',
  },
];

const OurStorySection = () => {
  const sectionRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [failedImages, setFailedImages] = useState({});

  const goToImage = (index, direction = 1) => {
    if (index === activeImage) return;
    setSlideDirection(direction);
    setActiveImage(index);
  };

  const goNext = () => {
    setSlideDirection(1);
    setActiveImage((prev) => (prev + 1) % STORY_IMAGES.length);
  };

  const goPrev = () => {
    setSlideDirection(-1);
    setActiveImage((prev) => (prev - 1 + STORY_IMAGES.length) % STORY_IMAGES.length);
  };

  const getCarouselOffset = (index) => {
    const total = STORY_IMAGES.length;
    let diff = index - activeImage;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;
    return diff;
  };

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      setIsActive(true);
      return undefined;
    }

    let done = false;
    const activate = () => {
      if (done) return;
      done = true;
      setIsActive(true);
    };

    const isInView = () => {
      const rect = section.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
    };

    if (isInView()) {
      activate();
      return undefined;
    }

    const fallback = window.setTimeout(activate, 700);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate();
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(section);

    const onScroll = () => {
      if (isInView()) {
        activate();
        observer.disconnect();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;

    const timer = window.setInterval(() => {
      setSlideDirection(1);
      setActiveImage((prev) => (prev + 1) % STORY_IMAGES.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [isActive]);

  const markImageFailed = (index) => {
    setFailedImages((prev) => ({ ...prev, [index]: true }));
  };

  const featured = STORY_IMAGES[activeImage];

  return (
    <section
      ref={sectionRef}
      className={`about-section about-story${isActive ? ' is-active' : ''}`}
      aria-labelledby="our-story-title"
    >
      <div className="story-ambient story-ambient--one" aria-hidden="true" />
      <div className="story-ambient story-ambient--two" aria-hidden="true" />
      <div className="story-grid-pattern" aria-hidden="true" />

      <div className="about-container">
        <header className="story-header story-reveal">
          {/* <span className="story-badge">Our Journey</span> */}
          <h2 id="our-story-title" className="story-heading">Our Journey</h2>
          <div className="story-heading-line" aria-hidden="true" />
          <p className="story-intro">
            From a bold idea to a nationwide platform — discover how 360Coordinates is reshaping
            property discovery with trust, technology, and transparency.
          </p>
        </header>

        <div className="story-layout">
          <div className="story-copy">
            <div className="story-cards">
              {MILESTONES.map((item, index) => (
                <article
                  key={item.step}
                  className={`story-card story-reveal story-reveal-delay-${index + 1}`}
                >
                  <div className="story-card-step">{item.step}</div>
                  <div className="story-card-content">
                    <h3 className="story-card-title">{item.title}</h3>
                    <p className="story-card-text">{item.text}</p>
                  </div>
                  <div className="story-card-glow" aria-hidden="true" />
                </article>
              ))}
            </div>

            <div className="story-tags story-reveal story-reveal-delay-4">
              {HIGHLIGHTS.map((label) => (
                <span key={label} className="story-tag">
                  <span className="story-tag-dot" aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="story-visual story-reveal story-reveal-delay-2">
            <div className="story-carousel">
              <div className="story-carousel-stage" aria-live="polite">
                {STORY_IMAGES.map((image, index) => {
                  const offset = getCarouselOffset(index);
                  const position =
                    offset === 0 ? 'center' : offset < 0 ? 'left' : offset > 0 ? 'right' : 'hidden';

                  return (
                    <button
                      key={image.src}
                      type="button"
                      className={`story-carousel-card story-carousel-card--${position}${
                        offset === 0 ? ' is-active' : ''
                      }`}
                      onClick={() => goToImage(index, index > activeImage ? 1 : -1)}
                      aria-label={`View ${image.tag}`}
                      aria-current={offset === 0 ? 'true' : undefined}
                      tabIndex={Math.abs(offset) <= 1 ? 0 : -1}
                    >
                      <span className="story-carousel-card-inner">
                        {!failedImages[index] ? (
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="story-carousel-img"
                            loading="lazy"
                            onError={() => markImageFailed(index)}
                          />
                        ) : (
                          <span className="story-carousel-fallback" aria-hidden="true" />
                        )}
                        <span className="story-carousel-card-shade" aria-hidden="true" />
                      </span>
                    </button>
                  );
                })}
              </div>

              <div
                key={`${activeImage}-${slideDirection}`}
                className={`story-carousel-info story-carousel-info--${
                  slideDirection > 0 ? 'next' : 'prev'
                }`}
              >
                <span className="story-carousel-info-tag">{featured.tag}</span>
                <p className="story-carousel-info-text">{featured.caption}</p>
              </div>

              <div className="story-carousel-controls">
                <button
                  type="button"
                  className="story-carousel-arrow story-carousel-arrow--prev"
                  onClick={goPrev}
                  aria-label="Previous image"
                >
                  ‹
                </button>

                <div className="story-carousel-dots" role="tablist" aria-label="Story gallery">
                  {STORY_IMAGES.map((image, index) => (
                    <button
                      key={image.src}
                      type="button"
                      role="tab"
                      className={`story-carousel-dot${activeImage === index ? ' is-active' : ''}`}
                      onClick={() => goToImage(index, index > activeImage ? 1 : -1)}
                      aria-selected={activeImage === index}
                      aria-label={image.tag}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="story-carousel-arrow story-carousel-arrow--next"
                  onClick={goNext}
                  aria-label="Next image"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStorySection;
